import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faMapMarkerAlt, faClock } from '@fortawesome/free-solid-svg-icons';

// Context
import { useI18n } from '../context/I18nContext';

const PageContainer = styled(Container)`
  padding-top: 2rem;
  padding-bottom: 4rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 900px) {
    grid-template-columns: 42fr 58fr;
    align-items: stretch;
    height: 100%;
  }
`;

const ContactForm = styled(Paper)`
  padding: 2rem;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
`;

const ContactInfo = styled(Paper)`
  padding: 2rem;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
`;

const ContactItem = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    margin-right: 1rem;
    color: var(--primary-color);
  }
`;

const ContactPage: React.FC = () => {
  const { t } = useI18n();
  
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    message: false,
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: false,
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {
      name: formState.name.trim() === '',
      email: !/^\S+@\S+\.\S+$/.test(formState.email),
      message: formState.message.trim() === '',
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Em um cenário real, enviaríamos os dados para um servidor
      console.log('Form data:', formState);
      
      // Mostrar mensagem de sucesso
      setSnackbar({
        open: true,
        message: 'Mensagem enviada com sucesso! Entraremos em contato em breve.',
        severity: 'success',
      });
      
      // Limpar formulário
      setFormState({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Por favor, corrija os erros no formulário antes de enviar.',
        severity: 'error',
      });
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageContainer maxWidth="lg">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" component="h1" align="center" gutterBottom color="primary">
            {t('nav_contact')}
          </Typography>
          
          <Typography variant="h6" align="center" paragraph color="textSecondary" sx={{ mb: 4 }}>
            Entre em contato conosco para mais informações sobre o TURoad, parcerias ou dúvidas.
          </Typography>
          
          <ContactGrid>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{ height: '100%' }}
            >
              <ContactInfo elevation={3}>
                <Typography variant="h5" gutterBottom color="primary">
                  Informações de Contato
                </Typography>
                
                <Box mt={3}>
                  <ContactItem>
                    <FontAwesomeIcon icon={faUser} size="lg" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Diretor Executivo
                      </Typography>
                      <Typography variant="body2">
                        Altair Bezerra de Araujo – CEO TURoad
                      </Typography>
                    </Box>
                  </ContactItem>
                  
                  <ContactItem>
                    <FontAwesomeIcon icon={faEnvelope} size="lg" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Email
                      </Typography>
                      <Typography variant="body2">
                        contato@trigen.com.br
                      </Typography>
                    </Box>
                  </ContactItem>
                  
                  <ContactItem>
                    <FontAwesomeIcon icon={faPhone} size="lg" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Telefone
                      </Typography>
                      <Typography variant="body2">
                        61-992111681
                      </Typography>
                    </Box>
                  </ContactItem>
                  
                  <ContactItem>
                    <FontAwesomeIcon icon={faMapMarkerAlt} size="lg" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Endereço
                      </Typography>
                      <Typography variant="body2">
                        Brasília, DF - Brasil
                      </Typography>
                    </Box>
                  </ContactItem>
                  
                  <ContactItem>
                    <FontAwesomeIcon icon={faClock} size="lg" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Horário de Atendimento
                      </Typography>
                      <Typography variant="body2">
                        Segunda a Sexta: 9h às 18h
                      </Typography>
                    </Box>
                  </ContactItem>
                </Box>
              </ContactInfo>
            </motion.div>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{ height: '100%' }}
            >
              <ContactForm elevation={3}>
                <Typography variant="h5" gutterBottom color="primary">
                  Envie-nos uma mensagem
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="Nome"
                      name="name"
                      value={formState.name}
                      onChange={handleInputChange}
                      margin="normal"
                      required
                      error={errors.name}
                      helperText={errors.name ? 'Nome é obrigatório' : ''}
                    />
                    
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formState.email}
                      onChange={handleInputChange}
                      margin="normal"
                      required
                      error={errors.email}
                      helperText={errors.email ? 'Email válido é obrigatório' : ''}
                    />
                    
                    <TextField
                      fullWidth
                      label="Assunto"
                      name="subject"
                      value={formState.subject}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                    
                    <TextField
                      fullWidth
                      label="Mensagem"
                      name="message"
                      value={formState.message}
                      onChange={handleInputChange}
                      margin="normal"
                      required
                      multiline
                      rows={4}
                      error={errors.message}
                      helperText={errors.message ? 'Mensagem é obrigatória' : ''}
                    />
                    
                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                      >
                        Enviar Mensagem
                      </Button>
                    </Box>
                  </Box>
                </form>
              </ContactForm>
            </motion.div>
          </ContactGrid>

        </motion.div>
      </PageContainer>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </motion.div>
  );
};

export default ContactPage;
